import { Component, Inject, OnInit, ViewChild } from "@angular/core";
import { MatCheckboxChange } from "@angular/material/checkbox";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { TranslateService } from "@ngx-translate/core";
import { UploadFile } from "@angular-ex/uploader";
import { Subscription } from "rxjs";
import {
  FileValidator,
  UploaderAcceptFormat,
} from "../../../../shared/components/file-uploader/file-uploader.component";
import { InvalidImageAspectRatioResponseError } from "../../../../shared/components/file-uploader/models/invalid-image-aspect-ratio-response-error";
import { UploaderResponseError } from "../../../../shared/components/file-uploader/models/uploader-response-error";
import { DefaultImageUploadMaxFileSize } from "../../../../shared/defaults/file-uploader-max-file-size";
import { ImageEntity } from "../../../../shared/entities/file/image.entity";
import { AppDataPostTags } from "../../../../shared/models/appData";
import { BrandCampaignAddContentDialogData } from "../../../../shared/models/brand-campaign-add-content-dialog-data";
import { BrandCampaign } from "../../../../shared/models/brandCampaign";
import { BrandCampaignCategory } from "../../../../shared/models/brandCampaignCategory";
import { BrandCampaignDetails } from "../../../../shared/models/brandCampaignDetail";
import { BrandCampaignFacebookPost } from "../../../../shared/models/brandCampaignFacebookPost";
import {
  CampaignPostContentType,
  PostPlatform,
} from "../../../../shared/enums/campaignPost.enums";
import { AppDataService } from "../../../../shared/services/api/app-data.service";
import { cloneWith } from "../../../../shared/services/clonable";
import { NotificationService } from "../../../../shared/services/notification.service";
import { BrandCampaignPostService } from "../../shared/services/brand-campaign-post.service";
import { InstagramAspectRatio } from "./instagram-aspect-ratio";
import { BrandCampaignContentFormPostSuggestedTextComponent } from "../brand-campaign-content-form-post-text/brand-campaign-content-form-post-suggested-text.component";
import { AnyToImageEntityMapper } from "../../../../shared/mappers/image.entity.mapper";

export interface CampaignContentFormPostImageDialogData
  extends BrandCampaignAddContentDialogData {
  readonly postContentType?: CampaignPostContentType;
  readonly post?: BrandCampaignFacebookPost;
}

export type CampaignContentFormPostImageDialogResult = undefined;

@Component({
  selector: "app-campaign-content-form-post-image-dialog",
  templateUrl: "./campaign-content-form-post-image-dialog.component.html",
  styleUrls: ["./campaign-content-form-post-image-dialog.component.scss"],
})
export class CampaignContentFormPostImageDialogComponent implements OnInit {
  public saving = false;
  public post: BrandCampaignFacebookPost;
  public displayedPostTags?: AppDataPostTags[];
  public uploadData: {
    media_type: string;
    platform: PostPlatform;
    post_id?: string;
  };
  public saveDisabled = false;
  public isDisableInstagramChannel = false;
  public readonly CampaignPostContentType = CampaignPostContentType;
  public readonly campaign: BrandCampaign;
  public readonly fileUploaderResponseMapper = AnyToImageEntityMapper;
  public readonly isNewPost: boolean;
  public readonly uploaderUrl: string;
  public readonly uploaderMimeTypes: string;
  public readonly acceptFormat: UploaderAcceptFormat;
  public readonly maxFileSizeInBytes = DefaultImageUploadMaxFileSize;
  @ViewChild("suggestedText")
  private suggestedText?: BrandCampaignContentFormPostSuggestedTextComponent;
  private currentFile?: File;

  private readonly campaignDetails: BrandCampaignDetails;

  private readonly category: BrandCampaignCategory;
  private readonly subs = new Subscription();
  public customFileValidators: FileValidator[] = [
    {
      validate: async (file: File) => this.checkImageAspectRatio(file),
    },
  ];

  constructor(
    private readonly translateService: TranslateService,
    private readonly brandCampaignPostService: BrandCampaignPostService,
    private readonly notificationService: NotificationService,
    private readonly dialogRef: MatDialogRef<
      CampaignContentFormPostImageDialogComponent,
      CampaignContentFormPostImageDialogResult
    >,
    private readonly appDataService: AppDataService,
    @Inject(MAT_DIALOG_DATA) data: CampaignContentFormPostImageDialogData,
  ) {
    this.campaign = data.campaign;
    this.campaignDetails = data.campaignDetails;
    this.category = data.category;
    this.isNewPost = !data.post;

    if (data.post) {
      this.post = cloneWith(data.post);
    } else {
      const contentType = data.postContentType ?? CampaignPostContentType.Image;
      this.post = BrandCampaignFacebookPost.create(
        this.validForInstagram(contentType),
        contentType,
      );
    }

    this.uploaderUrl =
      this.brandCampaignPostService.getAddCampaignDetailCategoryPostFileUrl(
        this.campaign.id,
        this.campaignDetails.id,
        this.category.id,
      );

    this.uploadData = {
      media_type: this.post.mediaType,
      platform: PostPlatform.Facebook,
    };

    if (this.post.id) {
      this.uploadData.post_id = this.post.id.toString();
    }

    if (this.post.mediaType === this.CampaignPostContentType.Gif) {
      this.acceptFormat = UploaderAcceptFormat.Gif;
      this.uploaderMimeTypes = "gif";
    } else {
      this.acceptFormat = UploaderAcceptFormat.Image;
      this.uploaderMimeTypes = "jpg, jpeg, png";
    }
  }

  public async ngOnInit(): Promise<void> {
    this.appDataService.get().then((appData) => {
      this.displayedPostTags = appData.postTags ?? [];
    });

    if (
      (this.post.mediaType === this.CampaignPostContentType.Image ||
        this.post.mediaType === this.CampaignPostContentType.Gif) &&
      this.post.image
    ) {
      await this.checkCurrentImageAspectRatio(this.post.image.toString());
    }

    this.checkSaveDisabled();
  }

  private async checkCurrentImageAspectRatio(imageUrl: string): Promise<void> {
    const imageFile = await this.blobUrlToFile(imageUrl);
    this.currentFile = imageFile;
    await this.checkImageAspectRatio(imageFile);
  }

  public async getBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });
  }

  public onSaveContent(): void {
    this.saving = true;
    if (this.isNewPost) {
      this.createPost();
    } else {
      this.savePost();
    }
  }

  public createPost(): void {
    this.subs.add(
      this.brandCampaignPostService
        .createCampaignDetailCategoryPost(
          this.campaign.id,
          this.campaignDetails.id,
          this.category.id,
          this.post,
        )
        .subscribe({
          next: (post) => {
            this.post = post;
            this.category.posts.push(this.post);
            this.dialogRef.close();
            this.notificationService.success("shared.changesSavedSuccessfully");
          },
          error: () => {
            this.saving = false;
            this.notificationService.error("shared.changesCouldNotBeSaved");
          },
        }),
    );
  }

  public savePost(): void {
    this.subs.add(
      this.brandCampaignPostService
        .updateCampaignDetailCategoryPost(
          this.campaign.id,
          this.campaignDetails.id,
          this.category.id,
          this.post,
        )
        .subscribe({
          next: (updatedPost) => {
            this.post = updatedPost;
            this.category.posts = this.category.posts.map((post) =>
              post.id === updatedPost.id ? updatedPost : post,
            );
            this.dialogRef.close();
            this.notificationService.success("shared.changesSavedSuccessfully");
          },
          error: () => {
            this.saving = false;
            this.notificationService.error("shared.changesCouldNotBeSaved");
          },
        }),
    );
  }

  public checkSaveDisabled(): void {
    if (
      (!this.post.postToFacebook && !this.post.postToInstagram) ||
      !this.post.image ||
      !this.post.text ||
      this.saving
    ) {
      this.saveDisabled = true;
    } else {
      this.saveDisabled = !this.suggestedText?.isValid;
    }
  }

  public textChanged(text: string): void {
    this.post.text = text;
    this.checkSaveDisabled();
  }

  public validForInstagram(mediaType: CampaignPostContentType) {
    if (mediaType === this.CampaignPostContentType.Image360) {
      return false;
    }
    return mediaType !== this.CampaignPostContentType.Gif;
  }

  public canPostToInstagram(): boolean {
    return this.validForInstagram(
      this.post.mediaType as CampaignPostContentType,
    );
  }

  public handleFileUploadResponse(file: ImageEntity): void {
    this.post.imageFileId = file.id;
    this.post.image = file.url;
    this.checkSaveDisabled();
    this.notificationService.success("shared.imageUploadedSuccessfully");
  }

  public fileAddedToQueue(file: UploadFile): void {
    this.currentFile = file.nativeFile;
    this.checkSaveDisabled();
  }

  public fileUploaderErrorHandler(error: UploaderResponseError): void {
    if (error instanceof InvalidImageAspectRatioResponseError) {
      this.isDisableInstagramChannel = true;
      this.post.postToInstagram = false;
    }

    this.notificationService.error(
      this.translateService.instant(error.message, error.interpolateParams),
    );
  }

  public async checkImageAspectRatio(file: File): Promise<boolean> {
    return new Promise((resolve, reject) => {
      this.getBase64(file).then((base64) => {
        const img = new Image();

        img.onload = () => {
          try {
            const aspect = img.width / img.height;
            if (
              aspect >= InstagramAspectRatio.MinAspectRatio &&
              aspect <= InstagramAspectRatio.MaxAspectRatio
            ) {
              this.isDisableInstagramChannel = false;
            } else {
              this.isDisableInstagramChannel = true;
              this.post.postToInstagram = false;
            }
            resolve(true);
          } catch (_err) {
            reject(new UploaderResponseError());
          }
        };

        img.onerror = () => {
          reject(new UploaderResponseError());
        };

        img.src = base64;
      });
    });
  }

  public async postToInstagramChange(
    isChecked: MatCheckboxChange,
  ): Promise<void> {
    if (isChecked && this.currentFile) {
      const isValid = await this.checkImageAspectRatio(this.currentFile).catch(
        (err: UploaderResponseError) => {
          if (err instanceof InvalidImageAspectRatioResponseError) {
            this.isDisableInstagramChannel = true;
            this.post.postToInstagram = false;
            this.checkSaveDisabled();
          } else {
            throw err;
          }
        },
      );

      if (isValid) {
        this.isDisableInstagramChannel = false;
      }
    }
    this.checkSaveDisabled();
  }

  private async blobUrlToFile(blobUrl: string): Promise<File> {
    return new Promise((resolve) => {
      fetch(blobUrl).then((res) => {
        res.blob().then((blob) => {
          const file = new File([blob], "", { type: blob.type });
          resolve(file);
        });
      });
    });
  }
}
